import { useState } from 'react';
import { Alert, Button, Group, Loader, Stack, Text, ThemeIcon } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck, IconClockHour4 } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { GoogleAds } from '@/shared/utils/googleAds';
import { CARD_ENTRY_ERROR_CODES, type IBillingPlanData, type ICardSummary } from '@/lib/types/Billing';
import { CardEntryUtils } from '../../CardEntryUtils';
import { useUpgradeTrialWithSavedCard } from '../../hooks/useCardEntry';
import { StripeCardForm, type CardFormError } from './StripeCardForm';

/** Contract §2.4 copy (ref_card_entry_contract.md). */
const PENDING_COPY =
  "Your card is saved and your upgrade is being processed. We'll email you as soon as your account is activated — usually within one business day.";
const PAYMENT_FAILED_COPY =
  "We couldn't charge your card. Please check the card details or use a different card, then try again.";
const CARD_REQUIRED_COPY = 'Please add a card before upgrading.';
const PAYMENT_FAILED_TITLE = 'Upgrade not completed';

type Phase =
  | { kind: 'card'; error: CardFormError | null }
  | { kind: 'activating' }
  | { kind: 'pending' }
  | { kind: 'error'; message: string };

interface TrialUpgradeStripeStepProps {
  parentCId: number;
  upgradePlan: IBillingPlanData;
}

/**
 * Trial → paid with FEATURE_FLAGS.billingCardEntryEnabled on (contract §2.4): save
 * the card through setup/confirm first, then POST order/upgradetrial with no card
 * data. `active` → provisioned now; `pending` → the engine is unreachable, the
 * operator finishes by hand; `payment_failed` → the engine reached Stripe and the
 * card declined: back to the card step (a new card + the same call charges the
 * existing Due invoice). Any other failure retries the upgrade call only.
 */
export function TrialUpgradeStripeStep({ parentCId, upgradePlan }: TrialUpgradeStripeStepProps) {
  const navigate = useNavigate();
  const upgradeMutation = useUpgradeTrialWithSavedCard();
  const [phase, setPhase] = useState<Phase>({ kind: 'card', error: null });
  const [savedCard, setSavedCard] = useState<ICardSummary | null>(null);
  const planLabel = upgradePlan.publicLabel || upgradePlan.name;

  async function activate() {
    setPhase({ kind: 'activating' });
    try {
      const res = await upgradeMutation.mutateAsync({ cId: parentCId, planId: upgradePlan.id });
      switch (res.activation) {
        case 'active':
          // Premium plans are keyed by "no stream limit" (legacy conversion-snippet parity)
          GoogleAds.trackTrialUpgradeConversion(upgradePlan.limitStreams == null);
          notifications.show({
            title: 'Upgrade Successful',
            message: `You have been upgraded to the ${planLabel} plan.`,
            color: 'green',
          });
          navigate({ to: '/account/usage' });
          return;
        case 'pending':
          setPhase({ kind: 'pending' });
          return;
        case 'payment_failed':
          setSavedCard(null);
          setPhase({
            kind: 'card',
            error: {
              title: PAYMENT_FAILED_TITLE,
              message: res.message ? `${PAYMENT_FAILED_COPY} (${res.message})` : PAYMENT_FAILED_COPY,
            },
          });
          return;
      }
    } catch (err) {
      if (CardEntryUtils.apiErrorCode(err) === CARD_ENTRY_ERROR_CODES.CARD_REQUIRED) {
        setSavedCard(null);
        setPhase({ kind: 'card', error: { title: PAYMENT_FAILED_TITLE, message: CARD_REQUIRED_COPY } });
        return;
      }
      setPhase({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to upgrade plan.' });
    }
  }

  if (phase.kind === 'card') {
    return (
      <StripeCardForm
        // Remount per attempt so a declined-at-charge retry starts from a fresh SetupIntent.
        key={phase.error?.message ?? 'first'}
        defaults={CardEntryUtils.billingDefaults(null)}
        initialError={phase.error}
        onSaved={(card) => {
          setSavedCard(card);
          return activate();
        }}
        onCancel={() => navigate({ to: '/account/usage' })}
        submitLabel="Upgrade Now"
      />
    );
  }

  return (
    <Stack gap="md">
      {savedCard && (
        <Group gap="xs">
          <ThemeIcon color="green" variant="light" size={22} radius="xl">
            <IconCheck size={14} />
          </ThemeIcon>
          <Text size="sm">{CardEntryUtils.cardSummaryLabel(savedCard)} saved as your payment method.</Text>
        </Group>
      )}

      {phase.kind === 'activating' && (
        <Group gap="xs">
          <Loader size="sm" />
          <Text size="sm">Activating your {planLabel} plan…</Text>
        </Group>
      )}

      {phase.kind === 'pending' && (
        <>
          <Alert icon={<IconClockHour4 size={16} />} color="blue" title="Your account is being activated">
            {PENDING_COPY}
          </Alert>
          <Group justify="flex-end">
            <Button onClick={() => navigate({ to: '/account/usage' })}>Go to Usage</Button>
          </Group>
        </>
      )}

      {phase.kind === 'error' && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Upgrade not completed">
          <Stack gap="xs" align="flex-start">
            <Text size="sm">Your card was saved, but the upgrade could not be completed ({phase.message}).</Text>
            <Button size="xs" variant="light" onClick={activate}>
              Try again
            </Button>
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}
